/**
 * Platform detection utilities for Capacitor mobile app.
 *
 * In remote mode, the Capacitor WebView loads our Vercel URL.
 * These helpers let components adapt behavior for native vs web
 * (e.g., skip cookie banners, use native share sheet).
 *
 * Detection strategy (ordered by reliability):
 * 1. window.Capacitor bridge (works when Capacitor JS runtime is loaded)
 * 2. The "Capacitor" user-agent suffix the app appends on purpose
 *    (`appendUserAgent` in capacitor.config.ts, iOS and Android)
 *
 * Deliberately NOT used: `navigator.standalone` (true for the site added to the
 * iOS home screen) and the Android "; wv)" flag (true for in-app browsers such
 * as LinkedIn or Instagram). Both are plain web visitors and must see the
 * cookie banner (ALU-399).
 */

type Platform = 'ios' | 'android' | 'web'

/** Suffix set by `appendUserAgent: 'Capacitor'` in capacitor.config.ts. */
const NATIVE_UA = /\bCapacitor\b/

/** Cache the result to avoid repeated detection */
let _cachedIsNative: boolean | null = null
let _cachedPlatform: Platform | null = null

/**
 * Check if running inside a Capacitor native WebView.
 * Safe to call on both server and client — returns false on server.
 *
 * Uses multiple detection strategies because in Capacitor remote mode
 * (server.url pointing to a web app), the Capacitor JS bridge may not
 * be fully injected by the time React components mount.
 */
export function isNativePlatform(): boolean {
  if (typeof window === 'undefined') return false
  if (_cachedIsNative !== null) return _cachedIsNative

  try {
    // Strategy 1: Capacitor bridge (most reliable when available)
    const cap = (window as unknown as Record<string, unknown>).Capacitor as
      | { isNativePlatform?: () => boolean }
      | undefined
    if (cap?.isNativePlatform?.()) {
      _cachedIsNative = true
      return true
    }

    // Strategy 2: the UA suffix appended by the native shell. The bridge may
    // not be injected yet when React mounts; the UA is there from the start.
    if (NATIVE_UA.test(navigator.userAgent)) {
      _cachedIsNative = true
      return true
    }

    _cachedIsNative = false
    return false
  } catch {
    _cachedIsNative = false
    return false
  }
}

/**
 * Get the current platform: 'ios', 'android', or 'web'.
 * Safe to call on server — returns 'web'.
 */
export function getPlatform(): Platform {
  if (typeof window === 'undefined') return 'web'
  if (_cachedPlatform !== null) return _cachedPlatform

  try {
    // Try Capacitor bridge first
    const cap = (window as unknown as Record<string, unknown>).Capacitor as
      | { getPlatform?: () => string }
      | undefined
    const platform = cap?.getPlatform?.()
    if (platform === 'ios' || platform === 'android') {
      _cachedPlatform = platform
      return platform
    }

    // Fallback: detect from user agent when in native platform
    if (isNativePlatform()) {
      const ua = navigator.userAgent
      if (/iPhone|iPad|iPod/.test(ua)) {
        _cachedPlatform = 'ios'
        return 'ios'
      }
      if (/Android/.test(ua)) {
        _cachedPlatform = 'android'
        return 'android'
      }
    }

    _cachedPlatform = 'web'
    return 'web'
  } catch {
    _cachedPlatform = 'web'
    return 'web'
  }
}

/** Convenience: true when running on iOS */
export function isIOS(): boolean {
  return getPlatform() === 'ios'
}

/** Convenience: true when running on Android */
export function isAndroid(): boolean {
  return getPlatform() === 'android'
}

/**
 * Reset cached values. Useful for testing or when
 * Capacitor bridge becomes available after initial load.
 */
export function resetPlatformCache(): void {
  _cachedIsNative = null
  _cachedPlatform = null
}
